import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

interface User {
    id: string | number;
}

interface MinioCredentials {
    endpoint: string;
    accessKey: string;
    secretKey: string;
    bucket: string;
}

interface MinioSettingsProps {
    user: User;
}

const MinioSettings: React.FC<MinioSettingsProps> = ({ user }) => {
    const [show, setShow] = useState<boolean>(false);
    const [credentials, setCredentials] = useState<MinioCredentials>({
        endpoint: '',
        accessKey: '',
        secretKey: '',
        bucket: ''
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [testingConnection, setTestingConnection] = useState<boolean>(false);
    const [connectionStatus, setConnectionStatus] = useState<{
        status: 'success' | 'error' | null;
        message: string;
    }>({ status: null, message: '' });
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (show) {
            fetchMinioCredentials();
        }
    }, [show]);

    const fetchMinioCredentials = async (): Promise<void> => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/minio-credentials/${user.id}`);
            const data = await response.json();
            setCredentials(data);
        } catch (err) {
            console.error('Failed to fetch Minio credentials:', err instanceof Error ? err.message : String(err));
            setConnectionStatus({
                status: 'error',
                message: 'Failed to load existing credentials. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await fetch(`/api/minio-credentials/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });
            setShow(false);
        } catch (err) {
            console.error('Failed to update Minio credentials:', err instanceof Error ? err.message : String(err));
            setConnectionStatus({
                status: 'error',
                message: 'Failed to save credentials. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        setCredentials(prev => ({
            ...prev,
            [name]: value
        }));
        // Reset connection status when credentials change
        setConnectionStatus({ status: null, message: '' });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
            try {
                const jsonData = JSON.parse(event.target?.result as string);
                setCredentials({
                    endpoint: jsonData.url || '',
                    accessKey: jsonData.accessKey || '',
                    secretKey: jsonData.secretKey || '',
                    bucket: ''
                });
                // Reset connection status when new credentials are loaded
                setConnectionStatus({ status: null, message: '' });
            } catch (err) {
                console.error('Error parsing JSON data-source:', err instanceof Error ? err.message : String(err));
                setConnectionStatus({
                    status: 'error',
                    message: 'Invalid JSON data-source format. Please check the data-source and try again.'
                });
            }
        };
        reader.readAsText(file);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const testConnection = async (): Promise<void> => {
        setTestingConnection(true);
        setConnectionStatus({ status: null, message: '' });

        try {
            const response = await fetch('/api/test-minio-connection', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });

            const data = await response.json();

            if (response.ok) {
                setConnectionStatus({
                    status: 'success',
                    message: 'Connection successful! All credentials are valid.'
                });
            } else {
                setConnectionStatus({
                    status: 'error',
                    message: data.message || 'Failed to connect to Minio server. Please check your credentials.'
                });
            }
        } catch (err) {
            console.error('Error testing connection:', err instanceof Error ? err.message : String(err));
            setConnectionStatus({
                status: 'error',
                message: 'Network error occurred while testing connection.'
            });
        } finally {
            setTestingConnection(false);
        }
    };

    return (
        <>
            <Button
                variant="link"
                className="p-0 border-0 d-flex align-items-center"
                onClick={() => setShow(true)}
                title="Storage Settings"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="text-secondary"
                    viewBox="0 0 16 16"
                >
                    <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
                    <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319z"/>
                </svg>
            </Button>

            <Modal show={show} onHide={() => setShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Minio Storage Settings</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <div className="mb-4">
                            <Form.Label>Upload Credentials JSON</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileUpload}
                                    ref={fileInputRef}
                                    className="d-none"
                                    id="credentials-upload"
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => document.getElementById('credentials-upload')?.click()}
                                    className="w-100"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        className="me-2"
                                        viewBox="0 0 16 16"
                                    >
                                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                        <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z"/>
                                    </svg>
                                    Upload credentials.json
                                </Button>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Endpoint</Form.Label>
                            <Form.Control
                                type="text"
                                name="endpoint"
                                value={credentials.endpoint}
                                onChange={handleChange}
                                placeholder="https://minio.example.com"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Access Key</Form.Label>
                            <Form.Control
                                type="password"
                                name="accessKey"
                                value={credentials.accessKey}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Secret Key</Form.Label>
                            <Form.Control
                                type="password"
                                name="secretKey"
                                value={credentials.secretKey}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Bucket Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="bucket"
                                value={credentials.bucket}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        {connectionStatus.status && (
                            <Alert variant={connectionStatus.status === 'success' ? 'success' : 'danger'}>
                                {connectionStatus.message}
                            </Alert>
                        )}

                        <div className="d-flex justify-content-start mb-3">
                            <Button
                                variant="outline-primary"
                                onClick={testConnection}
                                disabled={testingConnection || !credentials.endpoint || !credentials.accessKey || !credentials.secretKey}
                            >
                                {testingConnection ? 'Testing...' : 'Test Connection'}
                            </Button>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShow(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading || connectionStatus.status === 'error'}
                        >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default MinioSettings;